import {
  NAV_PLACEMENT,
  NAV_REQUIREMENT,
  NAV_ROUTE_ID,
  NAV_SECTION,
  NAV_STATUS_KEY,
} from './types/nav-enums';
import type { NavRouteDefinition } from './types/nav-types';

const NAV_ROUTE_MANIFEST: readonly NavRouteDefinition[] = [
  // --- Primary ---
  {
    id: NAV_ROUTE_ID.HOME,
    path: '/home',
    label: 'Home',
    pageTitle: 'Home',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.TITLE_ONLY,
    order: 1,
  },
  {
    id: NAV_ROUTE_ID.HOME_V2,
    path: '/homev2',
    label: 'Home v2',
    pageTitle: 'Home v2',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.TITLE_ONLY,
    order: 2,
  },

  // --- Boards ---
  {
    id: NAV_ROUTE_ID.REALTIME,
    path: '/board/realtime',
    label: 'Realtime',
    pageTitle: 'Realtime',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 10,
    requirements: [NAV_REQUIREMENT.REALTIME_ENV],
  },
  {
    id: NAV_ROUTE_ID.ATTRIBUTION,
    path: '/board/attribution',
    label: 'Attribution',
    pageTitle: 'Attribution',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 20,
    hideStandaloneTitle: true,
  },
  {
    id: NAV_ROUTE_ID.PNL_DASHBOARD,
    path: '/board/pnl/dashboard',
    label: 'Profit & Loss',
    pageTitle: 'P&L Dashboard',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 30,
    requirements: [NAV_REQUIREMENT.SHOPIFY_DATA],
  },
  {
    id: NAV_ROUTE_ID.INCOME_STATEMENT,
    path: '/board/pnl/income-statement',
    label: 'Income Statement',
    pageTitle: 'Income Statement',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 31,
    requirements: [NAV_REQUIREMENT.SHOPIFY_DATA],
  },
  {
    id: NAV_ROUTE_ID.META_REACH_ROLLING,
    path: '/board/reach/rolling',
    label: 'Meta Rolling Reach',
    pageTitle: 'Meta Reach',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 40,
  },
  {
    id: NAV_ROUTE_ID.META_REACH_COMPARISON,
    path: '/board/reach/comparison',
    label: 'Meta Rolling Comparison',
    pageTitle: 'Meta Reach',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 41,
  },
  {
    id: NAV_ROUTE_ID.META_REACH_INCREMENTAL,
    path: '/board/reach/incremental',
    label: 'Meta Incremental Reach',
    pageTitle: 'Meta Reach',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 42,
  },
  {
    id: NAV_ROUTE_ID.TRAFFIC,
    path: '/board/traffic-overview',
    label: 'Traffic',
    pageTitle: 'Traffic',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    parentId: NAV_ROUTE_ID.ATTRIBUTION,
    order: 21,
  },
  {
    id: NAV_ROUTE_ID.COHORTS,
    path: '/board/cohorts',
    label: 'LTV Cohort Summary',
    pageTitle: 'LTV Cohort Summary',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 60,
    requirements: [NAV_REQUIREMENT.SHOPIFY_DATA],
  },
  {
    id: NAV_ROUTE_ID.ECOMMERCE,
    path: '/board/ecommerce',
    label: 'Ecommerce',
    pageTitle: 'Ecommerce',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 70,
    requirements: [
      NAV_REQUIREMENT.LEGACY_DASHBOARDS_ENV,
      NAV_REQUIREMENT.SHOPIFY_DATA,
    ],
  },
  {
    id: NAV_ROUTE_ID.KPI,
    path: '/board/kpis',
    label: 'KPI Dashboard',
    pageTitle: 'KPI Dashboard',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 80,
    requirements: [NAV_REQUIREMENT.LEGACY_DASHBOARDS_ENV],
  },
  {
    id: NAV_ROUTE_ID.ORDERS,
    path: '/board/orders',
    label: 'Orders',
    pageTitle: 'Orders',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 90,
    requirements: [NAV_REQUIREMENT.BETA_FEATURE_ENV, NAV_REQUIREMENT.SHOPIFY_DATA],
  },
  {
    id: NAV_ROUTE_ID.PRODUCTS,
    path: '/board/products',
    label: 'Products',
    pageTitle: 'Products',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.BOARDS,
    order: 100,
    requirements: [NAV_REQUIREMENT.BETA_FEATURE_ENV, NAV_REQUIREMENT.SHOPIFY_DATA],
  },

  // --- Non-dashboard ---
  {
    id: NAV_ROUTE_ID.ACQUISITION,
    path: '/account/acquisition',
    label: 'Acquisition',
    pageTitle: 'Acquisition',
    matchPatterns: ['/account/acquisition/*'],
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.NON_DASHBOARD,
    order: 10,
    requirements: [
      NAV_REQUIREMENT.CONTACT_ACQUISITION_ENV,
      NAV_REQUIREMENT.CONTACT_ACQUISITION_FEATURE,
    ],
  },

  // --- Control panel ---
  {
    id: NAV_ROUTE_ID.EVENTS_MANAGER,
    path: '/events-manager',
    label: 'Events Manager',
    pageTitle: 'Events Manager',
    matchPatterns: ['/events-manager/*'],
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.CONTROL_PANEL,
    order: 10,
    statusKey: NAV_STATUS_KEY.EVENT_HEALTH,
  },
  {
    id: NAV_ROUTE_ID.META_EMQ,
    path: '/meta-data-quality',
    label: 'Meta EMQ',
    pageTitle: 'Meta EMQ',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.CONTROL_PANEL,
    order: 20,
    statusKey: NAV_STATUS_KEY.EMQ_HEALTH,
  },
  {
    id: NAV_ROUTE_ID.DESTINATIONS,
    path: '/destinations',
    label: 'Destinations',
    pageTitle: 'Destinations',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.CONTROL_PANEL,
    order: 30,
    statusKey: NAV_STATUS_KEY.DESTINATION_HEALTH,
  },
  {
    id: NAV_ROUTE_ID.DATA_SOURCES,
    path: '/integrations',
    label: 'Data Sources',
    pageTitle: 'Data Sources',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.CONTROL_PANEL,
    order: 40,
    statusKey: NAV_STATUS_KEY.INTEGRATION_HEALTH,
  },
  {
    id: NAV_ROUTE_ID.NOTIFICATIONS,
    path: '/notifications',
    label: 'Notifications',
    pageTitle: 'Notifications',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.CONTROL_PANEL,
    order: 50,
    // Unread badge is resolved via NavStatusSnapshot.unreadCount, not a health statusKey.
  },

  // --- Title-only / shell titles ---
  {
    id: NAV_ROUTE_ID.VIEWS,
    path: '/board/views',
    label: 'Views',
    pageTitle: 'Views',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.TITLE_ONLY,
    order: 2,
    hideStandaloneTitle: true,
  },
  {
    id: NAV_ROUTE_ID.GET_STARTED,
    path: '/get-started',
    label: 'Onboarding',
    pageTitle: 'Get started',
    placements: [NAV_PLACEMENT.DESKTOP, NAV_PLACEMENT.MOBILE],
    section: NAV_SECTION.TITLE_ONLY,
    order: 3,
    hideStandaloneTitle: true,
  },
  {
    id: NAV_ROUTE_ID.CUSTOM_DASHBOARD,
    path: '/board/custom-dashboard',
    label: 'Custom Dashboard',
    pageTitle: 'Custom Dashboard',
    placements: [NAV_PLACEMENT.DESKTOP],
    section: NAV_SECTION.TITLE_ONLY,
    order: 4,
    hideStandaloneTitle: true,
  },
  {
    id: NAV_ROUTE_ID.HOME_DASHBOARD,
    path: '/board/home-dashboard',
    label: 'Performance Dashboard (Legacy)',
    pageTitle: 'Performance Dashboard (Legacy)',
    placements: [NAV_PLACEMENT.DESKTOP],
    section: NAV_SECTION.TITLE_ONLY,
    order: 5,
  },
  {
    id: NAV_ROUTE_ID.PIXEL_SETUP,
    path: '/pixel',
    label: 'Setup',
    pageTitle: 'Setup',
    placements: [NAV_PLACEMENT.DESKTOP],
    section: NAV_SECTION.TITLE_ONLY,
    order: 6,
  },
  {
    id: NAV_ROUTE_ID.SHOPIFY_SETUP,
    path: '/setup/shopify',
    label: 'Shopify Setup',
    pageTitle: 'Shopify Setup',
    placements: [NAV_PLACEMENT.DESKTOP],
    section: NAV_SECTION.TITLE_ONLY,
    order: 7,
  },
  {
    id: NAV_ROUTE_ID.STRIPE_PLAN_SUCCESS,
    path: '/account/stripe-plan/success',
    label: 'Billing',
    pageTitle: 'Billing',
    placements: [NAV_PLACEMENT.DESKTOP],
    section: NAV_SECTION.TITLE_ONLY,
    order: 8,
  },

  // --- Settings (IA v2 chrome: Workspace / Tracking / Developer / Personal) ---
  {
    id: NAV_ROUTE_ID.SETTINGS_ROOT,
    path: '/account/settings',
    label: 'Settings',
    pageTitle: 'Settings',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 1,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_ACCOUNT,
    path: '/account/settings/account',
    label: 'Business Details',
    pageTitle: 'Business Details',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 10,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_TEAM,
    path: '/account/settings/team',
    label: 'Team',
    pageTitle: 'Team',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 20,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_BILLING,
    path: '/account/settings/billing',
    label: 'Billing',
    pageTitle: 'Billing',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 30,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_USAGE,
    path: '/account/settings/usage',
    label: 'Usage',
    pageTitle: 'Usage',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 31,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_PAYMENT_METHODS,
    path: '/account/settings/payment-methods',
    label: 'Payment methods',
    pageTitle: 'Payment methods',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 32,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_INVOICES,
    path: '/account/settings/invoices',
    label: 'Invoices',
    pageTitle: 'Invoices',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 33,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_PLANS,
    path: '/account/settings/plans',
    label: 'Plans',
    pageTitle: 'Plans',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 34,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_BILLING_V2,
    path: '/account/settings/billing/v2',
    label: 'Billing',
    pageTitle: 'Billing',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 35,
  },
  {
    id: NAV_ROUTE_ID.ACCOUNT_BILLING_SHOPIFY,
    path: '/account/billing',
    label: 'Billing',
    pageTitle: 'Billing',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_WORKSPACE,
    order: 36,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_UTM,
    path: '/account/settings/utm',
    label: 'UTM parameters',
    pageTitle: 'UTM parameters',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_TRACKING,
    order: 10,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_COSTS,
    path: '/account/settings/costs',
    label: 'Cost Settings',
    pageTitle: 'Cost Settings',
    matchPatterns: ['/account/settings/costs/*'],
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_TRACKING,
    order: 20,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_PIXEL,
    path: '/account/settings/pixel',
    label: 'Pixel Settings',
    pageTitle: 'Pixel Settings',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_TRACKING,
    order: 30,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_CUSTOM_EVENT_MAPPING,
    path: '/account/settings/custom-event-mapping',
    label: 'Custom event mapping',
    pageTitle: 'Custom event mapping',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_TRACKING,
    order: 31,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_DASHBOARD,
    path: '/account/settings/dashboard',
    label: 'Reporting',
    pageTitle: 'Reporting',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_TRACKING,
    order: 32,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_API_KEYS,
    path: '/account/settings/api-keys',
    label: 'API Keys',
    pageTitle: 'API Keys',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_DEVELOPER,
    order: 10,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_NOTIFICATIONS,
    path: '/account/settings/notifications',
    label: 'Notifications',
    pageTitle: 'Notifications',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_PERSONAL,
    order: 10,
  },
  {
    id: NAV_ROUTE_ID.SETTINGS_PASSWORD,
    path: '/account/settings/password',
    label: 'Password',
    pageTitle: 'Password',
    placements: [NAV_PLACEMENT.SETTINGS],
    section: NAV_SECTION.SETTINGS_PERSONAL,
    order: 20,
  },
];

export { NAV_ROUTE_MANIFEST };
